import { useState, useEffect, useRef } from "react";
import { Search, Download, CheckCircle2, XCircle, Award, Loader2 } from "lucide-react";
import api from "@/lib/api";
import PublicNav from "@/components/PublicNav";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";

function fmtDate(d) {
  if (!d || d.length !== 8) return d || "—";
  return `${d.slice(6, 8)}/${d.slice(4, 6)}/${d.slice(0, 4)}`;
}

const trackDownload = (diploma_id, callsign, extra = {}) =>
  api.post("/downloads", { diploma_id, callsign, ...extra }).catch(() => {});

function QslBandCard({ callsign, r, band }) {
  const [img, setImg] = useState(null);
  const [loading, setLoading] = useState(false);
  const generated = useRef(false);

  const generate = async () => {
    setLoading(true);
    try {
      const { data } = await api.post(
        `/diplomas/${r.diploma_id}/generate`,
        { callsign, band },
        { responseType: "blob" }
      );
      setImg(URL.createObjectURL(data));
    } catch {
      /* ignore */
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!generated.current) {
      generated.current = true;
      generate();
    }
    // eslint-disable-next-line
  }, []);

  return (
    <div className="mt-4" data-testid={`qsl-band-card-${r.diploma_id}-${band}`}>
      <p className="text-xs uppercase tracking-[0.25em] text-emerald-400 mb-2 font-mono font-semibold">
        Banda {band.toUpperCase()}
      </p>
      <div className="rounded-md overflow-hidden border border-zinc-700/50 bg-zinc-100 flex items-center justify-center min-h-[160px]">
        {loading ? (
          <Loader2 className="h-8 w-8 animate-spin text-amber-500 my-10" />
        ) : img ? (
          <img
            data-testid={`diploma-image-${r.diploma_id}-${band}`}
            src={img}
            alt={`QSL ${band}`}
            className="w-full h-auto"
          />
        ) : (
          <span className="text-zinc-500 text-sm py-10">Sin imagen</span>
        )}
      </div>
      {img && (
        <a
          href={img}
          download={`${callsign}_${r.name}_${band.toUpperCase()}.jpg`}
          data-testid={`download-${r.diploma_id}-${band}`}
          onClick={() => trackDownload(r.diploma_id, callsign, { band })}
        >
          <Button className="w-full mt-3 bg-emerald-600 hover:bg-emerald-500 text-white rounded-md h-11 font-semibold transition-colors">
            <Download className="h-4 w-4 mr-2" /> Descargar {band.toUpperCase()}
          </Button>
        </a>
      )}
    </div>
  );
}

function MultiLevelCard({ callsign, r, level }) {
  const [img, setImg] = useState(null);
  const [loading, setLoading] = useState(false);
  const generated = useRef(false);

  const generate = async () => {
    setLoading(true);
    try {
      const { data } = await api.post(
        `/diplomas/${r.diploma_id}/generate`,
        { callsign, level_index: level.index },
        { responseType: "blob" }
      );
      setImg(URL.createObjectURL(data));
    } catch {
      /* ignore */
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!generated.current) {
      generated.current = true;
      generate();
    }
    // eslint-disable-next-line
  }, []);

  return (
    <div className="mt-4" data-testid={`multilevel-card-${r.diploma_id}-${level.index}`}>
      <p className="text-xs uppercase tracking-[0.25em] text-emerald-400 mb-2 font-mono font-semibold">
        Nivel {level.name || `#${level.index + 1}`} · {level.required} contactos
      </p>
      <div className="rounded-md overflow-hidden border border-zinc-700/50 bg-zinc-100 flex items-center justify-center min-h-[160px]">
        {loading ? (
          <Loader2 className="h-8 w-8 animate-spin text-amber-500 my-10" />
        ) : img ? (
          <img
            data-testid={`diploma-image-${r.diploma_id}-${level.index}`}
            src={img}
            alt={`Diploma nivel ${level.name}`}
            className="w-full h-auto"
          />
        ) : (
          <span className="text-zinc-500 text-sm py-10">Sin imagen</span>
        )}
      </div>
      {img && (
        <a
          href={img}
          download={`${callsign}_${r.name}_${(level.name || "nivel" + (level.index + 1)).replace(/\s+/g, "_")}.jpg`}
          data-testid={`download-${r.diploma_id}-${level.index}`}
          onClick={() => trackDownload(r.diploma_id, callsign, { level_index: level.index })}
        >
          <Button className="w-full mt-3 bg-emerald-600 hover:bg-emerald-500 text-white rounded-md h-11 font-semibold transition-colors">
            <Download className="h-4 w-4 mr-2" /> Descargar {level.name || `Nivel ${level.index + 1}`}
          </Button>
        </a>
      )}
    </div>
  );
}

function DiplomaResult({ callsign, r }) {
  const [img, setImg] = useState(null);
  const [loading, setLoading] = useState(false);
  const generated = useRef(false);
  const pct = Math.min(100, Math.round((r.matched / (r.required || 1)) * 100));
  // For QSL type, if we have multiple bands, we render one card per band
  const isMultiBandQsl = r.type === "qsl" && Array.isArray(r.bands) && r.bands.length > 0;
  const isMultidiploma = r.type === "multidiploma";
  const achievedLevels = isMultidiploma
    ? (r.levels || []).filter((l) => l.achieved && l.has_template)
    : [];
  const highestAchieved = isMultidiploma
    ? (r.levels || []).slice().reverse().find((l) => l.achieved)
    : null;

  const generate = async () => {
    setLoading(true);
    try {
      const { data } = await api.post(
        `/diplomas/${r.diploma_id}/generate`,
        { callsign },
        { responseType: "blob" }
      );
      setImg(URL.createObjectURL(data));
    } catch {
      /* ignore */
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (r.eligible && !isMultiBandQsl && !isMultidiploma && !generated.current) {
      generated.current = true;
      generate();
    }
    // eslint-disable-next-line
  }, []);

  return (
    <div
      data-testid={`diploma-result-${r.diploma_id}`}
      className="rounded-lg bg-zinc-900/60 backdrop-blur-xl border border-zinc-700/50 p-6 animate-in fade-in slide-in-from-bottom-2 duration-500"
    >
      <div className="flex items-start justify-between gap-4 mb-4">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-zinc-500 mb-1">
            {r.type === "qsl" ? "QSL"
              : r.type === "multidiploma" ? "Multidiploma"
              : r.type === "activator" ? "Diploma Activador"
              : "Diploma"}
            {r.station_callsign ? ` · ${r.station_callsign}` : ""}
          </p>
          <h2 className="font-mono text-xl font-bold tracking-tight">{r.name}</h2>
          {r.subtitle && <p className="text-sm text-amber-500/80">{r.subtitle}</p>}
        </div>
        {r.eligible ? (
          <span className="flex items-center gap-1.5 text-emerald-400 bg-emerald-500/10 border border-emerald-500/30 rounded-full px-3 py-1 text-xs font-semibold uppercase tracking-wider whitespace-nowrap">
            <CheckCircle2 className="h-4 w-4" /> Conseguido
          </span>
        ) : (
          <span className="flex items-center gap-1.5 text-zinc-400 bg-zinc-800 border border-zinc-700 rounded-full px-3 py-1 text-xs font-semibold uppercase tracking-wider whitespace-nowrap">
            <XCircle className="h-4 w-4" /> Incompleto
          </span>
        )}
      </div>

      {isMultidiploma ? (
        <>
          <div className="flex items-center justify-between text-sm font-mono mb-2">
            <span className="text-zinc-400">Contactos válidos</span>
            <span className="text-amber-500 font-semibold">
              {r.matched}
              {highestAchieved ? ` · Nivel más alto: ${highestAchieved.name || `#${highestAchieved.index + 1}`}` : " · Sin nivel"}
            </span>
          </div>
          <div className="grid grid-cols-4 gap-1.5 mt-1">
            {(r.levels || []).map((lv) => (
              <div
                key={lv.index}
                className={`rounded-sm px-2 py-1.5 text-center text-[10px] font-mono border ${
                  lv.achieved
                    ? "bg-emerald-500/15 border-emerald-500/50 text-emerald-300"
                    : "bg-zinc-800/60 border-zinc-700 text-zinc-500"
                }`}
                data-testid={`level-badge-${r.diploma_id}-${lv.index}`}
              >
                <div className="uppercase tracking-wider">{lv.name || `N${lv.index + 1}`}</div>
                <div className="opacity-80">{r.matched} / {lv.required}</div>
              </div>
            ))}
          </div>
        </>
      ) : (
        <>
          <div className="flex items-center justify-between text-sm font-mono mb-2">
            <span className="text-zinc-400">
              {r.type === "activator" ? "Activaciones" : "Contactos válidos"}
            </span>
            <span className="text-amber-500 font-semibold">
              {r.matched} / {r.required}
            </span>
          </div>
          <Progress value={pct} className="h-2 bg-zinc-800 [&>div]:bg-amber-500" />
        </>
      )}

      {(r.required_band || r.required_mode) && (
        <p className="text-xs text-zinc-500 mt-3 font-mono">
          Requisito:{" "}
          {r.required_band && <span className="text-zinc-300">{r.required_band.toUpperCase()} </span>}
          {r.required_mode && <span className="text-zinc-300">{r.required_mode}</span>}
        </p>
      )}

      {!r.eligible && !isMultidiploma && r.matched < r.required && (
        <p className="text-sm text-zinc-400 mt-3">
          Te falta(n) <span className="text-amber-500 font-semibold">{r.required - r.matched}</span>{" "}
          {r.type === "activator" ? "activación(es)" : "contacto(s)"}.
        </p>
      )}
      {isMultidiploma && !r.eligible && (
        <p className="text-sm text-zinc-400 mt-3">
          Aún no alcanzas el nivel más bajo (necesitas al menos {r.required} contactos).
        </p>
      )}

      {r.contacts && r.contacts.length > 0 && (
        <div className="mt-4 border-t border-zinc-800 pt-4" data-testid={`contacts-list-${r.diploma_id}`}>
          <p className="text-xs uppercase tracking-[0.2em] text-zinc-500 mb-2">
            Contactos realizados ({r.matched})
          </p>
          <div className="grid grid-cols-[1fr_auto_auto] gap-x-4 gap-y-1 text-sm font-mono">
            <span className="text-xs uppercase tracking-wider text-zinc-600">Fecha</span>
            <span className="text-xs uppercase tracking-wider text-zinc-600 text-right">Banda</span>
            <span className="text-xs uppercase tracking-wider text-zinc-600 text-right">Modo</span>
            {r.contacts.map((c, i) => (
              <div key={i} className="contents">
                <span className="text-zinc-300">{fmtDate(c.qso_date)}</span>
                <span className="text-zinc-400 text-right">{(c.band || "—").toUpperCase()}</span>
                <span className="text-amber-500/90 text-right">{c.mode || "—"}</span>
              </div>
            ))}
          </div>
          {r.matched > r.contacts.length && (
            <p className="text-xs text-zinc-600 mt-2">…y {r.matched - r.contacts.length} más</p>
          )}
        </div>
      )}

      {isMultidiploma && r.eligible && achievedLevels.length > 0 && (
        <div className="mt-5">
          <p className="text-xs uppercase tracking-[0.2em] text-zinc-500 mb-1">
            {achievedLevels.length === 1
              ? "Diploma conseguido"
              : `Diplomas conseguidos (${achievedLevels.length}) — descarga los que quieras`}
          </p>
          {achievedLevels.map((lv) => (
            <MultiLevelCard key={lv.index} callsign={callsign} r={r} level={lv} />
          ))}
        </div>
      )}
      {isMultidiploma && r.eligible && achievedLevels.length === 0 && (
        <p className="text-sm text-amber-500/80 mt-4">
          Has alcanzado un nivel, pero aún no se ha subido la plantilla correspondiente. Contacta con el club.
        </p>
      )}

      {r.eligible && isMultiBandQsl && (
        <div className="mt-5">
          <p className="text-xs uppercase tracking-[0.2em] text-zinc-500 mb-1">
            {r.bands.length === 1
              ? "Tu tarjeta QSL"
              : `Se ha generado una QSL por cada banda (${r.bands.length})`}
          </p>
          {r.bands
            .slice()
            .sort((a, b) => (a.band || "").localeCompare(b.band || ""))
            .map((b) => (
              <QslBandCard key={b.band} callsign={callsign} r={r} band={b.band} />
            ))}
        </div>
      )}

      {r.eligible && !isMultiBandQsl && !isMultidiploma && (
        <div className="mt-5">
          <div className="rounded-md overflow-hidden border border-zinc-700/50 bg-zinc-100 flex items-center justify-center min-h-[160px]">
            {loading ? (
              <Loader2 className="h-8 w-8 animate-spin text-amber-500 my-10" />
            ) : img ? (
              <img data-testid={`diploma-image-${r.diploma_id}`} src={img} alt="Diploma" className="w-full h-auto" />
            ) : (
              <span className="text-zinc-500 text-sm py-10">Sin imagen</span>
            )}
          </div>
          {img && (
            <a href={img} download={`${callsign}_${r.name}.jpg`} data-testid={`download-${r.diploma_id}`} onClick={() => trackDownload(r.diploma_id, callsign)}>
              <Button className="w-full mt-3 bg-emerald-600 hover:bg-emerald-500 text-white rounded-md h-11 font-semibold transition-colors">
                <Download className="h-4 w-4 mr-2" /> Descargar
              </Button>
            </a>
          )}
        </div>
      )}
    </div>
  );
}

export default function PublicSearch() {
  const [callsign, setCallsign] = useState("");
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(null);
  const [error, setError] = useState("");
  const [available, setAvailable] = useState([]);

  useEffect(() => {
    api.get("/diplomas").then(({ data }) => setAvailable(data)).catch(() => {});
  }, []);

  const search = async (e) => {
    e?.preventDefault();
    const call = callsign.trim().toUpperCase();
    if (!call) return;
    setLoading(true);
    setError("");
    setResults(null);
    try {
      const { data } = await api.post("/verify", { callsign: call });
      setResults(data);
    } catch (err) {
      setError(err.response?.data?.detail || "Error al consultar");
    } finally {
      setLoading(false);
    }
  };

  // Count each unique band as one "conseguido" for QSL type; multidiploma counts achieved levels; others count as 1.
  const countUnits = (r, onlyEligible) => {
    if (r.type === "multidiploma") {
      const total = (r.levels || []).length || 4;
      const achieved = (r.levels || []).filter((l) => l.achieved).length;
      return onlyEligible ? achieved : total;
    }
    if (onlyEligible && !r.eligible) return 0;
    if (r.type === "qsl" && Array.isArray(r.bands) && r.bands.length > 0) return r.bands.length;
    return onlyEligible ? (r.eligible ? 1 : 0) : 1;
  };
  const eligibleCount = (results?.results || []).reduce((s, r) => s + countUnits(r, true), 0);
  const totalCount = (results?.results || []).reduce((s, r) => s + countUnits(r, false), 0);

  return (
    <div className="relative min-h-screen bg-[#D6D3D1] text-zinc-50 overflow-hidden">
      <PublicNav />
      <div className="relative z-10 max-w-3xl mx-auto px-5 py-10 md:py-16">
        <div className="animate-in fade-in slide-in-from-bottom-2 duration-500 pt-2">
          <p className="text-xs uppercase tracking-[0.3em] text-black font-semibold mb-3">Verificación de contactos</p>
          <form onSubmit={search} className="mb-4">
            <div className="rounded-lg bg-zinc-800/70 backdrop-blur-xl border border-zinc-600/60 p-5">
              <div className="flex flex-col sm:flex-row gap-3">
                <input
                  data-testid="callsign-search-input"
                  autoFocus
                  value={callsign}
                  onChange={(e) => setCallsign(e.target.value.toUpperCase())}
                  placeholder="Enter your callsign"
                  autoCapitalize="characters"
                  className="flex-1 bg-white text-zinc-900 rounded-md px-4 py-3 font-mono text-2xl sm:text-3xl font-bold tracking-wider outline-none placeholder:text-zinc-400 placeholder:font-normal placeholder:tracking-normal placeholder:text-lg focus:ring-2 focus:ring-[#5EA529]"
                />
                <Button
                  type="submit"
                  data-testid="search-submit-btn"
                  disabled={loading}
                  className="bg-[#5EA529] hover:bg-[#4f8c22] text-white font-semibold rounded-md h-14 px-8 text-base transition-colors"
                >
                  {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : <><Search className="h-5 w-5 mr-2" /> Comprobar</>}
                </Button>
              </div>
            </div>
          </form>

          {available.length > 0 && !results && (
            <p className="text-xs text-zinc-600 font-mono">
              {available.length} premio(s) disponible(s): {available.map((d) => d.name).join(" · ")}
            </p>
          )}
          {error && <p data-testid="search-error" className="text-red-400 text-sm font-mono">{error}</p>}
        </div>

        {results && (
            <div key={results.callsign} className="mt-8 space-y-4 animate-in fade-in duration-500">
              <div className="flex items-center gap-2 mb-2">
                <Award className="h-5 w-5 text-amber-500" />
                <h3 className="font-mono text-sm uppercase tracking-[0.2em] text-zinc-800">
                  {results.callsign} · {eligibleCount} conseguido(s) de {totalCount}
                </h3>
              </div>
              {results.results.length === 0 && (
                <p className="text-zinc-500 text-sm">No hay premios configurados todavía.</p>
              )}
              {results.results.map((r) => (
                <DiplomaResult key={r.diploma_id} callsign={results.callsign} r={r} />
              ))}
            </div>
        )}
      </div>
    </div>
  );
}
